import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import CommonTooltip from '../common-tooltip/CommonTooltip';
import clsx from 'clsx';

import { useSpeechChannel } from '../../context/SpeechChannelContext';

import './VoiceRecordButton.scss';

function VoiceRecordButton({ id, value, onChange, disabled }) {
  const root = 'voice-record-button';

  const committedLenRef = useRef(0);

  const {
    t,
    i18n: { language: i18nLang },
  } = useTranslation();

  const mappedLanguages = {
    en: 'en-US',
    de: 'de-DE',
  };

  const effectiveLang = mappedLanguages[i18nLang] || (typeof navigator !== 'undefined' ? navigator.language : 'en-US');

  const { transcript, resetTranscript, listening, browserSupportsSpeechRecognition, isMicrophoneAvailable } =
    useSpeechRecognition();

  const isDisabled = disabled || !isMicrophoneAvailable;

  const { activeId, setActiveId } = useSpeechChannel();
  const iAmOwner = activeId === id;
  const isActive = listening && iAmOwner;

  const appendDelta = useCallback(() => {
    if (!iAmOwner) return;

    const committedLen = committedLenRef.current || 0;
    if (!transcript || transcript.length <= committedLen) return;

    const delta = transcript.slice(committedLen);
    const trimmedDelta = delta;

    if (!trimmedDelta) return;

    const next = value + trimmedDelta;

    onChange(next);
    committedLenRef.current = transcript.length;
  }, [onChange, transcript, value, iAmOwner]);

  const startListening = useCallback(() => {
    setActiveId(id);
    committedLenRef.current = 0;
    resetTranscript();
    SpeechRecognition.startListening({ continuous: true, language: effectiveLang });
  }, [resetTranscript, effectiveLang, id, setActiveId]);

  const stopListening = useCallback(() => {
    if (!iAmOwner) return;
    appendDelta();
    SpeechRecognition.stopListening();
    resetTranscript();
    committedLenRef.current = 0;
    setActiveId(null);
  }, [iAmOwner, appendDelta, resetTranscript, setActiveId]);

  const toggleListening = useCallback(() => {
    if (isActive) {
      stopListening();
    } else {
      SpeechRecognition.abortListening();
      startListening();
    }
  }, [isActive, startListening, stopListening]);

  const listeningLabel = listening ? t('stopVoiceInput') : t('startVoiceInput');

  const statusText = useMemo(() => {
    if (!browserSupportsSpeechRecognition) return 'Speech recognition is not supported in this browser.';
    if (!isMicrophoneAvailable) return 'Microphone unavailable. Check permissions or device.';
    return listening ? 'Listening… speak now.' : 'Voice input is off.';
  }, [browserSupportsSpeechRecognition, isMicrophoneAvailable, listening]);

  useEffect(() => {
    if (!isActive) return;
    appendDelta();
  }, [transcript, isActive, appendDelta]);

  useEffect(() => {
    return () => {
      if (iAmOwner) {
        SpeechRecognition.abortListening();
        resetTranscript();
        committedLenRef.current = 0;
      }
    };
  }, [resetTranscript, iAmOwner]);

  return (
    <>
      <CommonTooltip title={listeningLabel}>
        <span>
          <button
            type='button'
            className={clsx(
              `${root}_button`,
              isActive && `${root}_button--active`,
              isDisabled && `${root}_button--disabled`,
            )}
            onClick={toggleListening}
            disabled={disabled || !isMicrophoneAvailable}
            aria-label={isDisabled}
            aria-pressed={isActive}
            aria-disabled={disabled || !isMicrophoneAvailable}>
            {isActive ? <MicOffRoundedIcon /> : <MicRoundedIcon />}
          </button>
        </span>
      </CommonTooltip>

      <div className={`${root}_sr`} aria-live='polite' id={id}>
        {statusText}
      </div>
    </>
  );
}

export default memo(VoiceRecordButton);
